import React, { useEffect, useRef } from 'react';

export const SLASH_MENU_ID = 'ai-assistant-slash-menu';
export const slashOptionId = (command) => `ai-assistant-slash-${command}`;

/**
 * Returns the partial command the user is typing ("/he" -> "he"), or null when
 * the "/" tips should not be shown (no leading slash, or already past the
 * command name).
 */
export function getSlashQuery(input) {
  const match = /^\/([\w-]*)$/.exec(String(input || ''));
  return match ? match[1].toLowerCase() : null;
}

/** Commands whose name or alias starts with the query. */
export function filterCommands(commands, query) {
  if (query === null || query === undefined) return [];
  const q = query.toLowerCase();
  return commands.filter(
    (c) => c.command.startsWith(q) || (c.aliases || []).some((a) => a.startsWith(q))
  );
}

/**
 * "/" tips dropdown shown above the message box. Purely presentational; the
 * parent owns keyboard handling and the highlighted index.
 */
const SlashCommandMenu = ({ commands, activeIndex, onSelect, onHover }) => {
  const listRef = useRef(null);

  useEffect(() => {
    const el = listRef.current?.children?.[activeIndex];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  if (!commands.length) {
    return (
      <div className="ai-assistant-slash-menu" role="status">
        <div className="ai-assistant-slash-empty">
          No matching shortcut. Type <code>/help</code> to see everything I can do.
        </div>
      </div>
    );
  }

  return (
    <div className="ai-assistant-slash-menu">
      <div className="ai-assistant-slash-heading" aria-hidden="true">
        Shortcuts · ↑↓ to navigate · Enter or Tab to select · Esc to close
      </div>
      <ul id={SLASH_MENU_ID} role="listbox" aria-label="Shortcuts" ref={listRef}>
        {commands.map((c, i) => (
          <li
            key={c.command}
            id={slashOptionId(c.command)}
            role="option"
            aria-selected={i === activeIndex}
            className={`ai-assistant-slash-option ${i === activeIndex ? 'active' : ''}`}
            // mousedown keeps focus in the textarea
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(c);
            }}
            onMouseEnter={() => onHover(i)}
          >
            <code className="ai-assistant-slash-usage">{c.usage}</code>
            <span className="ai-assistant-slash-desc">{c.description}</span>
            {c.example && c.example !== c.usage && (
              <span className="ai-assistant-slash-example">e.g. {c.example}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SlashCommandMenu;
